import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AudioLines,
  Copy,
  Settings2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useDeleteTtsFlow, useTtsFlowList } from '../api';
import { templateLabel, type TtsFlowListRow } from '../types';

export function TtsFlowListPage() {
  const navigate = useNavigate();
  const query = useTtsFlowList();
  const del = useDeleteTtsFlow();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<TtsFlowListRow | null>(
    null,
  );

  const columns = useMemo<ColumnDef<TtsFlowListRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'ttsFlowName',
        header: 'Flow',
        cell: ({ row }) => (
          <Link
            to={`/update-tts-flow/${row.original.id}`}
            className="flex items-center gap-2 font-medium text-foreground hover:underline"
          >
            <AudioLines className="h-3.5 w-3.5 text-muted-foreground" />
            {row.original.ttsFlowName}
          </Link>
        ),
      },
      {
        accessorKey: 'templateId',
        header: 'Template',
        size: 160,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {templateLabel(row.original.templateId)}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => {
          // Legacy: status === "0" → Active, anything else → Deleted.
          const active = String(row.original.status ?? '') === '0';
          return (
            <Badge
              variant={active ? 'default' : 'secondary'}
              className={
                active
                  ? 'bg-emerald-100 text-emerald-900 hover:bg-emerald-100'
                  : ''
              }
            >
              {active ? 'Active' : 'Deleted'}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'startDate',
        header: 'Start',
        size: 120,
        cell: ({ row }) => row.original.startDate || '—',
      },
      {
        accessorKey: 'endDate',
        header: 'End',
        size: 120,
        cell: ({ row }) => row.original.endDate || '—',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{row.original.ttsFlowName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-tts-flow/${row.original.id}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(`/clone-tts-flow/${row.original.id}`)}
              >
                <Copy className="h-4 w-4" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.ttsFlowName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the TTS flow.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Flows"
        description="Text-to-speech automations — play prompts, gather DTMF, optionally patch to agents."
        actions={
          <Button asChild>
            <Link to="/add-tts-flow">
              <Plus className="h-4 w-4" />
              New TTS flow
            </Link>
          </Button>
        }
      />

      <DataTable<TtsFlowListRow, unknown>
        tableId="tts-flow:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search flows…"
        emptyTitle="No TTS flows yet"
        emptyDescription="Create a TTS flow to play synthesized speech to your callers."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="tts-flows.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete TTS flow?"
        description={
          <>
            TTS flow{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.ttsFlowName}
            </span>{' '}
            will be deleted. Campaigns referencing it will stop working.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
